import * as React from 'react';

// Utils
import { searchSelectors } from '@src/shared/src/selectors';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { IRootState } from '@src/store';
import { findBySelected } from '@src/shared/src/util/general';
import {
  getHotelTariffDocuments,
  getHotelTariffOrderNumber,
  getPrimaryPictureURL,
} from '@src/shared/src/util/hotels';
import { formatDate, formatDayDifference } from '@src/shared/src/util/date';

// Constants
import { DATE_FORMAT_TYPES, STATUS } from '@src/shared/src/const/app';

// Actions
// Models
import { BasketItemModel, HotelFareModel, HotelModel } from '@src/shared/src/models';
// Interfaces
// Components
import { BookingInfoItem } from '@src/components/BookingInfoItem';
import ConfirmedBookingTravelInfoItem from './ConfirmedBookingTravelInfoItem';
import ConfirmedBookingDocuments from './ConfirmedBookingDocuments';
import ConfirmedBookingHeader from './ConfirmedBookingHeader';
import Badge from '@toolkit/ui/Badge';
import { FlexLaneBadge } from '@src/components/FlexLaneBadge';

// Styles
import '../styles/ConfirmedBooking.scss';

type Props = {
  hotel: HotelModel;
  basketItems: BasketItemModel[];
  basketStatus: string;
};

const ConfirmedHotel: React.FC<Props> = (props) => {
  const { hotel, basketItems, basketStatus } = props;
  const { t } = useTranslation();

  const search = useSelector((state: IRootState) => searchSelectors.search(state.search));

  const dayDifference = formatDayDifference(search.depAt, search.arrAt);
  const nights = `${dayDifference} ${dayDifference > 1 ? t('global.nights') : t('global.night')}`;

  const hotelSelectedFare: HotelFareModel = findBySelected(hotel.fares);
  const hotelSelectedFareId: number = Number(hotelSelectedFare?.id) ?? -1;

  return (
    <div className="confirmed-booking">
      <div className="confirmed-booking-inner">
        <ConfirmedBookingHeader
          title={hotel.name}
          image={getPrimaryPictureURL(hotel.primaryPictureURL, hotel.pictureURLs)}
          date={formatDate(search.depAt, DATE_FORMAT_TYPES.DAY_LONG_DATE)}
          headerBadge={<Badge label={nights} icon={'event'} />}
          fullImage={true}
        />
        <BookingInfoItem
          icon={<i className="material-symbols">bed</i>}
          depName={t('bookingDetails.label.hotel.checkIn')}
          arrName={t('bookingDetails.label.hotel.checkOut')}
          depAt={formatDate(search.depAt, DATE_FORMAT_TYPES.LONG_DATE_TIME)}
          arrAt={formatDate(search.arrAt, DATE_FORMAT_TYPES.LONG_DATE_TIME)}
        />
        <ConfirmedBookingTravelInfoItem
          icon={<i className="material-symbols">route</i>}
          title={t('bookingDetails.label.hotel.address')}
          value={hotel.address}
        />
        {hotel.phoneNumber && (
          <ConfirmedBookingTravelInfoItem
            icon={<i className="material-symbols">phone</i>}
            title={t('bookingDetails.label.hotel.phone')}
            value={hotel.phoneNumber}
          />
        )}
        <ConfirmedBookingDocuments
          documents={getHotelTariffDocuments(basketItems, hotelSelectedFareId)}
          basketStatus={basketStatus}
        />
      </div>

      <div className="confirmed-booking-travel-info">
        <div className="confirmed-booking-travel-info-title">
          {t('BookingTravelInfo.title.booking')}
        </div>

        {basketStatus === STATUS.CONFIRM_FINISHED && (
          <ConfirmedBookingTravelInfoItem
            icon={<i className="material-symbols">numbers</i>}
            title={t('bookingDetails.label.bookingId')}
            value={getHotelTariffOrderNumber(basketItems, hotelSelectedFareId)}
          />
        )}
        <ConfirmedBookingTravelInfoItem
          icon={<i className="material-symbols">group</i>}
          title={t('bookingDetails.label.guests')}
          value={search.passengers
            .map((passenger) => `${passenger.firstName} ${passenger.lastName}`)
            .join(', ')}
        />
        <ConfirmedBookingTravelInfoItem
          icon={<i className="material-symbols">hotel</i>}
          title={t('bookingDetails.label.room')}
          value={
            <>
              {hotelSelectedFare.roomType}
              {/* Temporary FLEXLANE marker (#14675) - delete with the badge component */}
              <FlexLaneBadge flexlane={hotelSelectedFare.flexlane} />
            </>
          }
        />
        <ConfirmedBookingTravelInfoItem
          icon={<i className="material-symbols">restaurant</i>}
          title={t('bookingDetails.label.breakfast')}
          value={
            hotelSelectedFare.breakfast
              ? t('bookingDetails.value.included')
              : t('bookingDetails.value.notIncluded')
          }
        />
        {hotelSelectedFare.conditions && (
          <ConfirmedBookingTravelInfoItem
            icon={<i className="material-symbols">event_busy</i>}
            title={t('bookingDetails.label.cancellation')}
            value={hotelSelectedFare.conditions}
          />
        )}
      </div>
    </div>
  );
};

export default ConfirmedHotel;
